import React, { useMemo } from 'react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import { sortRosterDetails, type RosterSortColumn } from '@/hooks/TableSettings/assignmentRoster.utils'
import { StatusPill } from '../shared/StatusPill'
import { toResultsDetails, type ResultsDetails } from '../assignments/SubmissionResultsDialog'

const S = PartnerService.AssignmentDetailsStatus

interface PersonAssignmentsRosterProps {
  person: UserService.Person
  details: PartnerService.AssignmentDetails[]
  getAssignmentTaskName: (assignmentId: string) => string
  isTaskLoading: boolean
  onViewResults: (d: ResultsDetails) => void
}

export const PersonAssignmentsRoster: React.FC<PersonAssignmentsRosterProps> = ({
  person, details, getAssignmentTaskName, isTaskLoading, onViewResults,
}) => {
  const { t } = useTar()
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || person.id

  const { sortColumn, sortDirection, toggleSort, getSortState } = useTableSettings({
    tableId: `cohortPersonAssignments-${person.id}`,
    defaultSort: { columnKey: 'startDate', direction: 'desc' },
  })

  // The Assignment (task name) column isn't sortable; only Started / Submitted / Status are.
  const rows = useMemo(
    () => sortRosterDetails(details, [], sortColumn, sortDirection),
    [details, sortColumn, sortDirection]
  )

  const sortButton = (col: RosterSortColumn, label: string) => (
    <button
      onClick={() => toggleSort(col)}
      className="group flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
    >
      <span>{label}</span>
      {renderSortIcon(getSortState(col))}
    </button>
  )

  if (details.length === 0) {
    return (
      <div className="border-t border-border/40 px-4 py-3 ps-4 text-xs text-muted-foreground lg:ps-12">
        {t('partner.noAssignmentsYet')}
      </div>
    )
  }

  return (
    <div className="border-t border-border/40">
      <div className="grid h-9 grid-cols-[1fr_auto] items-center gap-2 px-4 ps-4 text-[10px] uppercase tracking-wider text-muted-foreground lg:grid-cols-[1.6fr_1fr_1fr_140px] lg:ps-12">
        <span>{t('partner.assignment')}</span>
        <span className="hidden lg:block">{sortButton('startDate', t('common.started'))}</span>
        <span className="hidden lg:block">{sortButton('submitDate', t('common.submitted'))}</span>
        <span className="flex items-center justify-end">
          {sortButton('status', t('common.status'))}
        </span>
      </div>
      {rows.map((d, i) => {
        const graded = !!d.feedback && d.grade !== undefined
        const processing = d.status === S.Submitted && !graded
        const openResults = graded ? () => onViewResults(toResultsDetails(d, name, person)) : undefined
        return (
          <div
            key={`${d.assignmentId}-${i}`}
            className={cn(
              'grid h-12 grid-cols-[1fr_auto] items-center gap-2 border-b border-border/30 px-4 ps-4 text-xs last:border-b-0 lg:grid-cols-[1.6fr_1fr_1fr_140px] lg:ps-12',
              graded && 'cursor-pointer hover:bg-primary/5'
            )}
            {...(graded
              ? {
                  role: 'button' as const,
                  tabIndex: 0,
                  onClick: openResults,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openResults?.() }
                  },
                }
              : {})}
          >
            <span className="min-w-0 truncate font-medium text-foreground">
              {isTaskLoading ? t('common.loading') : getAssignmentTaskName(d.assignmentId)}
            </span>
            <span className="hidden text-foreground lg:block">{d.startDate ? <RelativeDateTime isoDateString={d.startDate} className="text-xs" /> : <span className="text-muted-foreground">—</span>}</span>
            <span className="hidden text-foreground lg:block">{d.submitDate ? <RelativeDateTime isoDateString={d.submitDate} className="text-xs" /> : <span className="text-muted-foreground">—</span>}</span>
            <span className="flex items-center justify-end gap-2">
              {graded ? (
                <GradeBadge grade={d.grade as number} small />
              ) : (
                <StatusPill status={d.status} processing={processing} joined={person.onboardingStatus === 'joined'} />
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}
